import React, { useMemo } from 'react';
import { Portrait, RoleUtils } from '@galaxy/lorule-react-commons';
import { Column } from 'react-table';
import { Button } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { Approver } from '../../../api/actionMemoApiTypes';
import DataGrid from '../../common/datatables/DataGrid';
import { renderDateCell } from '../../../utils/displayUtils';
import SkipApproverModal from '../modal/SkipApproverModal';
import {
    AmStatusType,
    ApproverRoutingStatusType,
    CONTRACT_SIGNER_ORIGIN,
} from '../../../utils/Constants';

interface ApproversProps {
    amId: number;
    amStatus: string;
    approvers: Approver[];
    refresh: () => void;
}

export const generateActionString = (routingStatus: string) => {
    switch (routingStatus) {
        case ApproverRoutingStatusType.APPROVED:
            return <span className=" text-success">Approved</span>;
        case ApproverRoutingStatusType.CURRENT:
            return (
                <span>
                    <strong>Current </strong>
                </span>
            );
        case ApproverRoutingStatusType.SKIPPED:
            return <span className=" text-warning">Skipped</span>;
        default:
            return '--';
    }
};

enum SKIP_STATUS {
    CAN_SKIP = 'SKIP',
    NO_SKIP = '',
    UN_SKIP = 'UNSKIP',
}

const Approvers = (props: ApproversProps) => {
    const { instance } = useMsal();
    const roles = RoleUtils.getRoles(instance);

    const getSkipStatus = (approver: Approver) => {
        switch (approver.routingStatus) {
            case ApproverRoutingStatusType.SKIPPED:
                return SKIP_STATUS.UN_SKIP;
            case ApproverRoutingStatusType.CURRENT:
            case ApproverRoutingStatusType.NOT_APPROVED:
                return SKIP_STATUS.CAN_SKIP;
            default:
                return SKIP_STATUS.NO_SKIP;
        }
    };

    const approvers = props.approvers.map((approver) => ({
        ...approver,
        skip: getSkipStatus(approver),
    }));

    const maxStopNumber =
        props.approvers.length > 0
            ? props.approvers
                  .map((approver) => approver.stopNumber)
                  .reduce((previous, current) => (current > previous ? current : previous), 0)
            : -1;

    const columns = React.useMemo(
        () => [
            {
                Header: '',
                accessor: 'stopNumber',
                disableSortBy: true,
                xs: 1,
                sm: 1,
                xl: 1,
                customClass: 'mobile-hide',
            },
            {
                Header: '',
                accessor: 'approverEmailAddress',
                Cell: (cellProps: { value: string }) =>
                    useMemo(
                        () => (
                            <div className="profile-photo">
                                <Portrait username={cellProps.value} roundedCircle />
                            </div>
                        ),
                        [],
                    ),
                disableSortBy: true,
                xs: 3,
                sm: 3,
                xl: 2,
            },
            {
                Header: 'Name / Title',
                accessor: 'approverName',
                Cell: (cellProps: {
                    value: string;
                    row: {
                        original: {
                            approverTitle: string;
                            origin: string;
                        };
                    };
                }) =>
                    useMemo(
                        () => (
                            <>
                                <div>{cellProps.value}</div>
                                <div className="font-italic font-weight-light">
                                    <small>{cellProps.row.original.approverTitle}</small>
                                </div>
                                <div>
                                    {cellProps.row.original.origin === CONTRACT_SIGNER_ORIGIN ? (
                                        <small>
                                            <strong className="text-danger">Contract Signer</strong>
                                        </small>
                                    ) : null}
                                </div>
                            </>
                        ),
                        [],
                    ),
                disableSortBy: true,
                xs: 9,
                sm: 9,
                xl:
                    RoleUtils.checkRole(roles, ['ActionMemo.Admin']) &&
                    (props.amStatus === AmStatusType.Pending ||
                        props.amStatus === AmStatusType.Hold)
                        ? 3
                        : 4,
            },
            {
                Header: 'Status',
                accessor: 'routingStatus',
                Cell: (cellProps: { value: string }) => generateActionString(cellProps.value),
                disableSortBy: true,
                xs: 6,
                sm: 6,
                xl: 2,
            },

            {
                Header: 'Acted On',
                accessor: 'actionDate',
                disableSortBy: true,
                Cell: renderDateCell,
                xs: 6,
                sm: 6,
                xl: 2,
            },
            ...(RoleUtils.checkRole(roles, ['ActionMemo.Admin']) &&
            (props.amStatus === AmStatusType.Pending || props.amStatus === AmStatusType.Hold)
                ? [
                      {
                          Header: '',
                          accessor: 'approverId',
                          disableSortBy: true,
                          Cell: (cellProps: {
                              row: {
                                  original: {
                                      approverName: string;
                                      approverId: number;
                                      stopNumber: number;
                                      skip: string;
                                  };
                              };
                          }) =>
                              useMemo(() => {
                                  if (cellProps.row.original.stopNumber !== maxStopNumber) {
                                      if (cellProps.row.original.skip === SKIP_STATUS.CAN_SKIP) {
                                          return (
                                              <SkipApproverModal
                                                  title="Skip Approver"
                                                  toggleButton={
                                                      <Button
                                                          variant="outline-primary"
                                                          className="ml-auto"
                                                          size="sm"
                                                      >
                                                          Skip
                                                      </Button>
                                                  }
                                                  amId={props.amId}
                                                  approverName={cellProps.row.original.approverName}
                                                  approverId={cellProps.row.original.approverId}
                                                  skip
                                                  onSubmit={props.refresh}
                                              />
                                          );
                                      }
                                      if (cellProps.row.original.skip === SKIP_STATUS.UN_SKIP) {
                                          return (
                                              <SkipApproverModal
                                                  title="Unskip Approver"
                                                  toggleButton={
                                                      <Button
                                                          variant="outline-primary"
                                                          className="ml-auto"
                                                          size="sm"
                                                      >
                                                          Unskip
                                                      </Button>
                                                  }
                                                  amId={props.amId}
                                                  approverName={cellProps.row.original.approverName}
                                                  approverId={cellProps.row.original.approverId}
                                                  skip={false}
                                                  onSubmit={props.refresh}
                                              />
                                          );
                                      }
                                  }
                                  return null;
                              }, []),
                          xs: 1,
                          sm: 1,
                          xl: 2,
                          customClass: 'am-list-action',
                      },
                  ]
                : []),
        ],
        [],
    );

    return (
        <div className="react-table px-2">
            <DataGrid
                columns={columns as Column<Object>[]}
                data={approvers}
                noGlobalFilter
                noPagination
                initialState={{ pageSize: 1000 }}
                mobileShowList
            />
        </div>
    );
};

export default Approvers;
